<template>
  <div>
    <div>
      <px-view-header title="Cadastro de compra" title-class="font-weight-bold"
                      :breadcrumbs="breadcrumbs">
        <template v-slot:before>
          <v-btn class="mb-4 mr-1" icon x-large color="primary" @click="onClickBack">
            <v-icon>
              mdi-arrow-left
            </v-icon>
          </v-btn>
        </template>
      </px-view-header>
      <div v-if="loadingForm">
        <px-circular-loading/>
      </div>
      <v-stepper v-if="!loadingForm && compra"
                 class="elevation-1 rounded-card"
                 v-model="currentStep"
                 vertical>

        <v-stepper-step :complete="currentStep > 1" step="1">
          Condições e frete
        </v-stepper-step>
        <v-stepper-content step="1">
          <px-cadastro-compra-dados-basicos
            is-from-requisicao
            usa-tabela-preco
            :compra="compra"
            :erp-campos-integracao="erpCamposIntegracao"
            :campos-obrigatorios="camposObrigatorios"
            :campos-readonly="camposReadonly"
            :campos-bloqueados-edicao="camposBloqueadorEdicaoRequisicao"
            @change-fornecedor="getDetalhesFornecedor"
            @ready="next"
            @cancel="onClickCancel"/>
        </v-stepper-content>

        <v-stepper-step :complete="currentStep > 2" step="2">Notificações</v-stepper-step>
        <v-stepper-content step="2">
          <px-notificar-fornecedor :compra="compra"
                                   :campos-personalizados="camposPersonalizados"
                                   @remove="onRemoverMensagem"
                                   @ready="next"
                                   @previous="previous"/>
        </v-stepper-content>

        <v-stepper-step :complete="currentStep > 3" step="3">Produtos</v-stepper-step>
        <v-stepper-content step="3">
          <px-cadastro-compra-produtos usa-tabela-preco
                                       is-from-requisicao
                                       :compra="compra"
                                       :hide-acoes="hideAcoes"
                                       :campos-readonly="camposReadonly"
                                       :erp-campos-integracao="erpCamposIntegracao"
                                       :campos-obrigatorios="camposObrigatorios"
                                       :loading="loading"
                                       @ready="onClickSalvar"
                                       @previous="previous"
                                       @save-draft="onSaveDraft"/>
        </v-stepper-content>
      </v-stepper>
      <px-confirm-dialog ref="confirm"/>
    </div>
  </div>
</template>

<script>
import { cloneDeep, isEmpty } from 'lodash';
import { mapGetters } from 'vuex';
import { ErpCamposIntegracao } from 'px-business-components';
import { CasaDecimalUtils } from 'px-components';
import CompraService from '@/modules/compra/compra-service';
import pxCadastroCompraProdutos from '@/modules/compra/cadastro/CadastroCompraProdutos.vue';
import pxNotificarFornecedor from '@/modules/compra/cadastro/CadastroNotificarFornecedor.vue';
import pxCadastroCompraDadosBasicos from '@/modules/compra/cadastro/CadastroCompraDadosBasicos.vue';
import {
  hasCadastrosAdicionaisErp,
} from '@/modules/carrinho/finalizacao/campos-erp/validate-campos-erp';
import {
  hasCadastrosAdicionaisProdutoErp,
} from '@/modules/produtos/campos-erp/helpers/validate-campos-erp';
import GestaoFornecedorService from '@/modules/gestao-fornecedor/gestao-fornecedor-service';
import {
  buildCadastrosAdicionaisPayload,
  getCadastroAdicional,
} from '@/utils/cadastros-adicionais-utils';
import { getTipoAplicacaoIcms, getTipoAplicacaoIpi } from '@/utils/imposto-utils';
import { filterProdutoNaoHomologado } from '@/utils/fornecedor-produto-utils';
import {
  currencyDP,
  calcularTotaisCompra,
  calcularTotaisSaving,
  calculatePercentualDescontoProdutos,
  sumSavingProdutos,
} from '@/modules/compra/cadastro/calculo-compra.service';
import EntidadeCamposCompraService
  from '@/modules/erp-campos/entidade-campos-compra-service';
import EntidadeCamposPersonalizadosService
  from '@/modules/campos-personalizados/entidade-campos-personalizados-service';
import CadastrosAdicionais from '@/enums/cadastros-adicionais';
import { setCamposPersonalizados } from '@/modules/campos-personalizados/campos-personalizado-utils';
import TipoNotaEntradaService from '@/modules/tipo-nota-entrada/tipo-nota-entrada-service';
import { deepNotEquals } from '@/utils/object-utils';
import TiposEntidadeCadastroAdicional from '@/enums/tipos-entidade-cadastro-adicional';
import EntidadeCamposRequisicaoService
  from '@/modules/erp-campos/entidade-campos-requisicao-service';
import { computeInitialRequisicaoCompra } from '../cadastro/compra-utils';

export default {
  components: { pxCadastroCompraDadosBasicos, pxNotificarFornecedor, pxCadastroCompraProdutos },
  data() {
    return {
      currentStep: 1,
      idRequisicao: this.$route.query.idRequisicao,
      idsRequisicoes: this.$route.query.idsRequisicoes,
      isRascunho: this.$route.params.isRascunho,
      idCompra: null,
      loadingForm: false,
      loading: false,
      cadastrosAdicionais: null,
      camposObrigatorios: null,
      camposPersonalizados: null,
      camposReadonly: null,
      camposBloqueadorEdicaoRequisicao: null,
      compra: null,
      initialCompra: {},
      breadcrumbs: [
        { text: 'Gestão de compras' },
        {
          text: 'Compras',
          to: '/compras',
        },
        { text: 'Cadastro de compras' },
      ],
    };
  },
  computed: {
    ...mapGetters('entidade', ['erpCamposIntegracao', 'idEntidade']),
    messageConfirm() {
      return this.isRascunho ? 'Você deseja atualizar este rascunho?' : 'Você deseja salvar esta compra como rascunho?';
    },
    hasCadastrosAdicionais() {
      return this.compra.cadastrosAdicionais !== null
        || hasCadastrosAdicionaisErp(this.erpCamposIntegracao, this.compra.cadastrosAdicionais);
    },
    hasCadastrosAdicionaisProduto() {
      return produto => produto.cadastrosAdicionais !== null
        || hasCadastrosAdicionaisProdutoErp(
          this.erpCamposIntegracao,
          produto.cadastrosAdicionais,
        );
    },
    isFormDirty() {
      if (this.loadingForm) {
        return false;
      }
      return Object.keys(this.initialCompra)
        .some(key => deepNotEquals(this.compra, this.initialCompra, key));
    },
    hideAcoes() {
      return this.erpCamposIntegracao === ErpCamposIntegracao.keys.SENIOR_MEGA.key;
    },
    casaDecimal() {
      const mappedProdutos = this.compra.produtos
        .map(produto => produto.produto)
        .filter(produto => produto);
      return CasaDecimalUtils.getCasaDecimal(mappedProdutos);
    },
  },
  methods: {
    setInitialCompra() {
      this.initialCompra = cloneDeep(this.compra);
    },
    onRemoverMensagem(item) {
      this.compra.mensagensPersonalizadas = this.compra
        .mensagensPersonalizadas.filter(mensagem => mensagem.id !== item.id);
    },
    onSaveDraft() {
      this.saveDraft();
    },
    onClickCancel() {
      if (this.idRequisicao) {
        this.$router.push({
          name: 'main.requiscao.detalhes',
          params: {
            id: this.idRequisicao,
          },
        });
        return;
      }
      this.$router.push({ name: 'main.compras' });
    },
    next() {
      this.currentStep = this.currentStep + 1;
    },
    previous() {
      this.currentStep = this.currentStep - 1;
    },
    getCompra() {
      if (this.idRequisicao) {
        return this.getCompraFromRequisicao();
      }
      return this.getCompraFromRequisicoes();
    },
    getCompraFromRequisicoes() {
      const requisicoesProdutos = this.idsRequisicoes.split(',').map(id => ({
        id: Number(id),
      }));
      return CompraService.getFromRequisicaoProdutos(requisicoesProdutos)
        .then(({ data }) => {
          this.compra = computeInitialRequisicaoCompra(data, this.erpCamposIntegracao);
          this.compra = setCamposPersonalizados(this.compra, this.camposPersonalizados);
          this.getNotaEntradaDefault();
        });
    },
    getCompraFromRequisicao() {
      return CompraService.getFromRequisicao(this.idRequisicao)
        .then(({ data }) => {
          this.compra = computeInitialRequisicaoCompra(data, this.erpCamposIntegracao);
          this.compra = setCamposPersonalizados(this.compra, this.camposPersonalizados);
          this.getNotaEntradaDefault();
        });
    },
    onClickBack() {
      this.$refs.confirm.open(this.messageConfirm,
        'O rascunho ficara disponível na aba RASCUNHOS e TODAS da listagem de compras.',
        { width: 450 })
        .then((confirm) => {
          if (!confirm) {
            this.$emit('update');
            if (this.idRequisicao) {
              this.$router.push({
                name: 'main.requiscao.detalhes',
                params: {
                  id: this.idRequisicao,
                },
              });
              return;
            }
            this.$router.back();
            return;
          }
          this.$emit('update');
          this.saveDraft();
        });
    },
    buildCadastrosAdicionaisProdutoPayload(produto) {
      if (!this.hasCadastrosAdicionaisProduto(produto)) {
        return [];
      }
      return buildCadastrosAdicionaisPayload(
        produto.cadastrosAdicionais,
        TiposEntidadeCadastroAdicional.keys.COMPRA_PRODUTO.key,
      );
    },
    buildCadastrosAdicionaisPayload() {
      if (!this.hasCadastrosAdicionais) {
        return [];
      }
      return buildCadastrosAdicionaisPayload(
        this.compra.cadastrosAdicionais,
        TiposEntidadeCadastroAdicional.keys.COMPRA.key,
      );
    },
    buildCompra() {
      const compra = { ...this.compra };
      compra.saving = calcularTotaisSaving(compra).saving;
      compra.produtos = compra.produtos.map(produto => ({
        ...produto,
        id: produto.id ? produto.id : undefined,
        impostos: {
          ...produto.impostos,
          tipoAplicacaoIcms: getTipoAplicacaoIcms(produto.impostos.icms),
          tipoAplicacaoIpi: getTipoAplicacaoIpi(produto.impostos.ipi),
        },
        cadastrosAdicionais: this.buildCadastrosAdicionaisProdutoPayload(produto),
        tipoNotaEntrada: compra.tipoNotaEntrada || null,
      }));
      return {
        ...compra,
        mensagem: compra.mensagem,
        cadastrosAdicionais: this.buildCadastrosAdicionaisPayload(),
      };
    },
    buildDraftCompra() {
      const compra = { ...this.compra };
      compra.produtos = compra.produtos.filter(produto => !!produto.produto);
      compra.produtos = compra.produtos.map(produto => ({
        ...produto,
        id: produto.id ? produto.id : undefined,
        impostos: {
          ...produto.impostos,
          tipoAplicacaoIcms: getTipoAplicacaoIcms(produto.impostos.icms),
          tipoAplicacaoIpi: getTipoAplicacaoIpi(produto.impostos.ipi),
        },
        cadastrosAdicionais: this.buildCadastrosAdicionaisProdutoPayload(produto),
        tipoNotaEntrada: compra.tipoNotaEntrada,
      }));
      return {
        ...compra,
        mensagem: compra.mensagem,
        cadastrosAdicionais: this.buildCadastrosAdicionaisPayload(),
      };
    },
    saveDraft() {
      this.loading = true;
      CompraService.saveRascunho(this.buildDraftCompra())
        .then(() => {
          this.$toast('Rascunho salvo com sucesso', { color: 'success' });
          this.$router.push({ name: 'main.compras' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getProdutosNaoHomologados() {
      return this.compra.produtos
        .map(p => p.produto)
        .filter(p => filterProdutoNaoHomologado(p, this.compra.fornecedor.id));
    },
    validateSaving() {
      const descontoItens = sumSavingProdutos(this.compra.produtos);
      const descontoGeral = currencyDP(this.compra.saving || 0);
      if (Number(descontoItens) === Number(descontoGeral)) {
        return true;
      }
      if (Number(descontoGeral) < 0.01) {
        this.compra.saving = Number(descontoItens);
        return true;
      }
      return this.$refs.confirm.open('O desconto geral da compra, adicionado na primeira etapa, é diferente do somatório dos descontos dos itens.',
        `Deseja alterar o desconto geral da compra (${this.$options.filters.currency(descontoGeral, this.simboloMoeda, this.casaDecimal)})
         para o somatório dos descontos dos itens (${this.$options.filters.currency(descontoItens, this.simboloMoeda, this.casaDecimal)}) e salvar?`,
        { width: 450 })
        .then((confirm) => {
          if (confirm) {
            this.compra.saving = Number(descontoItens);
            return true;
          }
          return false;
        });
    },
    async onClickSalvar() {
      if (currencyDP(
        calcularTotaisCompra(this.compra, this.compra.produtos).total,
        this.casaDecimal,
      ) <= 0) {
        const errorMessage = 'Total da compra deve ser maior que zero para salvar.';
        this.$toast(errorMessage, { color: 'error' });
        return;
      }
      const isValid = await this.validateSaving();
      if (!isValid) {
        return;
      }
      const produtos = this.getProdutosNaoHomologados();
      if (!isEmpty(produtos)) {
        const errorMessage = produtos.length > 1 ? `Fornecedor da compra não é homologado aos produtos: ${produtos.map(p => p.descricao).join(', ')}` : `Fornecedor da compra não é homologado ao produto: ${produtos[0].descricao.trim()}`;
        this.$toast(errorMessage, { color: 'error' });
        return;
      }
      this.loading = true;
      CompraService.save(this.buildCompra())
        .then(({ data }) => {
          this.$toast('Compra criada com sucesso', { color: 'success' });

          if (data.tipo === 'APROVACAO') {
            this.$router.push({
              name: 'main.aprovacao-compra.detalhes',
              params: {
                id: data.id,
              },
            });
            return;
          }

          this.$router.push({
            name: 'main.compras.detalhes',
            params: { id: data.id },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    beforeWindowUnload(e) {
      if (this.isFormDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    getDetalhesFornecedor(id) {
      return GestaoFornecedorService.getByFornecedor(id)
        .then(({ data }) => {
          // fazer função auxiliar quando tiver mais opções de campos adicionais
          this.compra.cadastrosAdicionais.natureza = getCadastroAdicional(data.cadastrosAdicionais,
            CadastrosAdicionais.keys.NATUREZA.key);
        });
    },
    getNotaEntradaDefault() {
      if (this.compra.tipoNotaEntrada !== null) {
        this.compra.produtos = this.compra.produtos.map(produto => ({
          ...produto,
          tipoNotaEntrada: produto.tipoNotaEntrada ?? this.compra.tipoNotaEntrada,
        }));
        return Promise.resolve();
      }
      return TipoNotaEntradaService.getNotaEntradaDefault()
        .then((response) => {
          this.compra.tipoNotaEntrada = response.data || null;
          this.compra.produtos = this.compra.produtos.map(produto => ({
            ...produto,
            tipoNotaEntrada: produto.tipoNotaEntrada ?? this.compra.tipoNotaEntrada,
          }));
        });
    },
    getCamposObrigatorios() {
      return EntidadeCamposCompraService.getCamposObrigatorios(this.erpCamposIntegracao)
        .then((data) => {
          this.camposObrigatorios = data;
        });
    },
    getCamposReadonly() {
      return EntidadeCamposCompraService.getCamposReadonly(this.erpCamposIntegracao)
        .then((data) => {
          this.camposReadonly = data;
        });
    },
    async getCamposPersonalizados() {
      try {
        EntidadeCamposPersonalizadosService
          .getCamposPersonalizadosCompraById(this.idEntidade)
          .then((campos) => {
            this.camposPersonalizados = campos;
          });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    /**
     * Carregamos os campos que são bloqueados para edição na requisição de compra.
     * Como esses campos não podem ser editados na edição da requisição, também precisamos
     * bloquear a edição desses campos no momento da criação da compra a partir de uma requisição.
     */
    getCamposBloqueadosEdicao() {
      return EntidadeCamposRequisicaoService.getCamposBloqueadosEdicao({
        idEntidade: this.idEntidade,
        erpCamposIntegracao: this.erpCamposIntegracao,
      })
        .then((data) => {
          this.camposBloqueadorEdicaoRequisicao = data;
        });
    },
  },
  watch: {
    currentStep(newVal, oldVal) {
      if (newVal === 3 && oldVal !== 3) {
        calculatePercentualDescontoProdutos(this.compra);
      }
    },
  },
  async created() {
    this.loadingForm = true;

    await this.getCamposPersonalizados();

    Promise.all([
      this.getCamposReadonly(),
      this.getCamposObrigatorios(),
      this.getCamposBloqueadosEdicao(),
      this.getCompra(),
    ])
      .finally(async () => {
        this.setInitialCompra();
        this.loadingForm = false;
      });
    window.addEventListener('beforeunload', this.beforeWindowUnload);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload);
  },
};
</script>
